import { useState } from 'react';
import { useIntl } from 'react-intl';
import Badge from '@mui/material/Badge';
import IconButton, { type IconButtonProps } from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined';
import SyncIcon from '@mui/icons-material/Sync';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import { useSyncStatus } from '../features/sync/useSyncStatus';
import { useOnlineStatus } from '../features/sync/useOnlineStatus';
import SyncDetailsDialog from './SyncDetailsDialog';

/**
 * Whether the device is keeping up with the server, in one button.
 *
 * Offline-first means a write ALWAYS succeeds locally, so this is the only
 * place that says whether it has left the device yet. Never an error to act
 * on: the queue drains itself once the connection returns.
 *
 * A round icon button like the account button beside it — a pill of words cost
 * the width of a sentence in the one place that has none to spare, and on a
 * phone it pushed the Maßnahmenname out of the title. It is a button in EVERY
 * state: no icon can say which documents are queued, who wrote them, or
 * whether this is the window that syncs, and the dialog behind it can.
 */
export default function SyncStatusButton({
    sx
}: {
    sx?: IconButtonProps['sx'];
}) {
    const intl = useIntl();
    const online = useOnlineStatus();
    const status = useSyncStatus();
    const { pending, active, hasError, blockedBy, replicatingHere } = status;
    const [detailsOpen, setDetailsOpen] = useState(false);

    function label() {
        if (!online) {
            return pending > 0
                ? intl.formatMessage(
                      { id: 'layout.sync.offlinePending' },
                      { count: pending }
                  )
                : intl.formatMessage({ id: 'layout.sync.offline' });
        }
        // Before `active`: a held queue is a standing state, and the flicker
        // of the retry behind it must not hide the one line that asks
        // somebody to do something.
        if (blockedBy.length > 0) {
            return intl.formatMessage(
                { id: 'layout.sync.blocked' },
                { names: blockedBy.join(', ') }
            );
        }
        if (active) {
            return pending > 0
                ? intl.formatMessage(
                      { id: 'layout.sync.syncingCount' },
                      { count: pending }
                  )
                : intl.formatMessage({ id: 'layout.sync.syncing' });
        }
        if (hasError) {
            return intl.formatMessage({ id: 'layout.sync.error' });
        }
        if (pending > 0) {
            return intl.formatMessage(
                { id: 'layout.sync.pending' },
                { count: pending }
            );
        }
        return intl.formatMessage({ id: 'layout.sync.synced' });
    }

    function icon() {
        if (!online) {
            return <CloudOffIcon />;
        }
        if (blockedBy.length > 0) {
            return <HowToRegIcon />;
        }
        if (hasError && !active) {
            return <ErrorOutlineIcon />;
        }
        if (active) {
            return (
                <SyncIcon
                    sx={{
                        animation: 'spin 1s linear infinite',
                        '@keyframes spin': {
                            '100%': { transform: 'rotate(360deg)' }
                        }
                    }}
                />
            );
        }
        return <CloudDoneIcon />;
    }

    // The state as a colour — the `.light` end of each, because this sits on the
    // app bar's own colour and `success.main` on dark blue is a dark green smudge.
    function color(): string {
        if (!online) {
            return 'warning.light';
        }
        // Waiting on a colleague's sign-in is a task, not a fault.
        if (blockedBy.length > 0) {
            return 'warning.light';
        }
        // Only a settled error is red; a running sync may still clear it.
        if (hasError && !active) {
            return 'error.light';
        }
        if (pending > 0) {
            return 'warning.light';
        }
        return 'success.light';
    }

    /**
     * The tooltip carries what the button no longer says out loud. Several
     * windows share one database and only one of them talks to the server (see
     * useSyncStatus), so a window where nothing ever spins gets the sentence
     * that explains why.
     */
    const hint = [
        label(),
        ...(replicatingHere
            ? []
            : [intl.formatMessage({ id: 'layout.sync.otherWindow' })]),
        intl.formatMessage({ id: 'syncError.open' })
    ].join(' · ');

    return (
        <>
            <Tooltip title={hint}>
                <IconButton
                    // The colour belongs to the icon, not to the button: MUI's
                    // `color` prop would tint the ripple and the focus ring
                    // with it too.
                    color="inherit"
                    aria-label={label()}
                    onClick={() => setDetailsOpen(true)}
                    sx={[
                        { color: color() },
                        ...(Array.isArray(sx) ? sx : [sx])
                    ]}
                >
                    <Badge
                        badgeContent={pending}
                        color="warning"
                        overlap="circular"
                        invisible={pending === 0}
                    >
                        {icon()}
                    </Badge>
                </IconButton>
            </Tooltip>
            <SyncDetailsDialog
                open={detailsOpen}
                onClose={() => setDetailsOpen(false)}
                status={status}
            />
        </>
    );
}
